import React from 'react';
import styled, { keyframes } from 'styled-components';
import Layout from '../../components/Layout';
import Footer from '../../components/Footer';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: 
    radial-gradient(circle at 20% 50%, rgba(110, 168, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(45, 108, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(58, 143, 255, 0.08) 0%, transparent 50%),
    linear-gradient(135deg, #0a0f2e 0%, #1a2451 50%, #0e1633 100%);
  position: relative;
  overflow: hidden;
  padding-bottom: 0;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.03;
  background-image: 
    linear-gradient(45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(-45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #6ea8ff 75%),
    linear-gradient(-45deg, transparent 75%, #6ea8ff 75%);
  background-size: 60px 60px;
  background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
  pointer-events: none;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 6rem 1.5rem 4rem 1.5rem;
  position: relative;
  z-index: 2;
  animation: ${fadeIn} 1s ease-out;
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 4rem 1.5rem 3rem 1.5rem;
  }
  @media (max-width: 480px) {
    padding: 3rem 1rem 2rem 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  color: #fff;
  text-shadow: 0 0 16px #6ea8ff, 0 2px 10px rgba(0, 0, 0, 0.5);
  animation: ${fadeIn} 0.8s ease-out;
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;
  background: rgba(20, 30, 60, 0.7);
  backdrop-filter: blur(12px);
  border: 2px solid rgba(45, 108, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 
      0 20px 40px rgba(45, 108, 255, 0.2),
      0 8px 16px rgba(45, 108, 255, 0.1);
    border-color: rgba(45, 108, 255, 0.4);
  }
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
  @media (max-width: 480px) {
    margin-bottom: 1.2rem;
    &:hover {
      transform: translateY(-3px);
    }
  }
`;

const SectionTitle = styled.div`
  padding: 1.5rem;
  font-weight: 700;
  font-size: 1.15rem;
  color: #fff;
  background: linear-gradient(135deg, rgba(45, 108, 255, 0.2) 0%, rgba(58, 143, 255, 0.1) 100%);
  border-bottom: 1px solid rgba(45, 108, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::before {
    content: '';
    display: block;
    width: 4px;
    height: 20px;
    background: linear-gradient(135deg, #6ea8ff 0%, #3a8fff 100%);
    border-radius: 2px;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    font-size: 1rem;
  }
  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.95rem;
    line-height: 1.4;
  }
`;

const SectionContent = styled.div`
  padding: 1.5rem;
  color: #b6d0ff;
  line-height: 1.6;
  @media (max-width: 768px) {
    padding: 1.2rem;
    line-height: 1.5;
  }
  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.95rem;
  }
  p {
    margin-bottom: 1rem;
    &:last-child {
      margin-bottom: 0;
    }
  }
  a {
    color: #6ea8ff;
    text-decoration: none;
    transition: color 0.3s ease;
    &:hover {
      text-decoration: underline;
      color: #3a8fff;
    }
  }
  ul, ol {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
    li {
      margin-bottom: 0.5rem;
      strong {
        color: #fff;
      }
    }
  }
  code {
    background: rgba(22, 32, 74, 0.8);
    color: #b6d0ff;
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: monospace;
    border: 1px solid rgba(45, 108, 255, 0.2);
  }
`;

const Troubleshooting = () => {
  return (
    <Layout>
      <PageWrapper>
        <BackgroundPattern />
        <Container>
          <PageTitle>Troubleshooting Guide</PageTitle>
        
          <Section>
            <SectionTitle>Common Issues</SectionTitle>
            <SectionContent>
              <ul>
                <li><strong>Application Won't Start:</strong> Ensure AdsPower is running and accessible. Try reinstalling the application and check your system meets the minimum requirements.</li>
                <li><strong>License Activation Issues:</strong> Check your internet connection, verify your license key, and contact support if the issue persists.</li>
                <li><strong>AdsPower Integration Problems:</strong> Make sure AdsPower is running, using the default port, and you can open profiles manually.</li>
                <li><strong>Profile Warming Issues:</strong> Ensure profiles are properly configured, proxies are working, and you can access target platforms through AdsPower.</li>
                <li><strong>Performance Issues:</strong> Check CPU/RAM usage, reduce simultaneous profiles, and ensure AdsPower has enough resources.</li>
              </ul>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>Error Messages</SectionTitle>
            <SectionContent>
              <ul>
                <li><strong>'Failed to connect to AdsPower':</strong> Check if AdsPower is running and accessible. Restart both applications if necessary.</li>
                <li><strong>'Invalid License Key':</strong> Verify your license key and ensure you have an active internet connection.</li>
                <li><strong>'Profile Access Denied':</strong> Check AdsPower permissions and ensure the profile isn't being used by another application.</li>
              </ul>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>Still Need Help?</SectionTitle>
            <SectionContent>
              <p>If you're still experiencing issues, our support team is here to help:</p>
              <ul>
                <li>
                  <a href="https://t.me/deserved0" target="_blank" rel="noopener noreferrer">
                      @deserved0
                    </a>
                    <br />
                  <a href="https://t.me/tomnookbank" target="_blank" rel="noopener noreferrer">
                      @tomnookbank
                    </a>
                </li>
                <li>
                  <strong>Debug Logs:</strong> When contacting support, please include your debug logs to help us resolve your issue faster.
                </li>
              </ul>
            </SectionContent>
          </Section>
        </Container>
      <Footer />
      </PageWrapper>
    </Layout>
  );
};

export default Troubleshooting; 